import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Textarea, Select } from '../ui/Input';
import { supportApi } from '../../services/api';
import { formatDateTime, getStatusColor, getPriorityColor } from '../../lib/utils';
import type { SupportTicket, TicketMessage } from '../../types';
import { User, Clock, Mail, Phone, Send, AlertCircle } from 'lucide-react';

interface TicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  onUpdate: (ticket: SupportTicket) => void;
}

const statusOptions = [
  { value: 'open', label: 'Opið' },
  { value: 'in_progress', label: 'Í vinnslu' },
  { value: 'waiting_customer', label: 'Bíður viðskiptavinar' },
  { value: 'resolved', label: 'Leyst' },
  { value: 'closed', label: 'Lokað' },
];

const priorityOptions = [
  { value: 'low', label: 'Lágur' },
  { value: 'medium', label: 'Miðlungs' },
  { value: 'high', label: 'Hár' },
  { value: 'critical', label: 'Brýnt' },
];

export function TicketDetailModal({
  isOpen,
  onClose,
  ticketId,
  onUpdate,
}: TicketDetailModalProps) {
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');

  useEffect(() => {
    if (isOpen && ticketId) {
      loadTicketDetails();
    }
  }, [isOpen, ticketId]);

  async function loadTicketDetails() {
    setLoading(true);
    setError(null);
    try {
      const response = await supportApi.getTicket(ticketId);
      setTicket(response.ticket);
      setMessages(response.messages);
      setSelectedStatus(response.ticket.status);
      setSelectedPriority(response.ticket.priority);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við að sækja beiðni');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSubmitting(true);
    try {
      const response = await supportApi.addMessage(ticketId, replyMessage, isInternal);
      setMessages(response.messages);
      setReplyMessage('');
      setIsInternal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Villa við að senda skilaboð');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateStatus() {
    if (!ticket) return;

    try {
      const response = await supportApi.updateTicket(ticketId, {
        status: selectedStatus,
        priority: selectedPriority,
      });
      setTicket(response.ticket);
      onUpdate(response.ticket);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Villa við að uppfæra beiðni');
    }
  }

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Hleður..." size="lg">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-litla-primary border-t-transparent"></div>
        </div>
      </Modal>
    );
  }

  if (error || !ticket) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Villa" size="lg">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error || 'Beiðni fannst ekki'}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Beiðni #${ticket.id.slice(0, 8)}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* Header Info */}
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-900">{ticket.subject}</h3>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDateTime(ticket.created_at)}
              </div>
              <Badge className={getStatusColor(ticket.status)}>
                {statusOptions.find((s) => s.value === ticket.status)?.label}
              </Badge>
              <Badge className={getPriorityColor(ticket.priority)}>
                {priorityOptions.find((p) => p.value === ticket.priority)?.label}
              </Badge>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="rounded-lg bg-gray-50 p-4">
          <h4 className="mb-3 text-sm font-medium text-gray-900">Viðskiptavinur</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-400" />
              <span>{ticket.customer_name}</span>
            </div>
            {ticket.customer_email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <span>{ticket.customer_email}</span>
              </div>
            )}
            {ticket.customer_phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{ticket.customer_phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <h4 className="mb-2 text-sm font-medium text-gray-900">Lýsing</h4>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
        </div>

        {/* Status & Priority Controls */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Staða"
            options={statusOptions}
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          />
          <Select
            label="Forgangur"
            options={priorityOptions}
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
          />
        </div>

        {(selectedStatus !== ticket.status || selectedPriority !== ticket.priority) && (
          <Button onClick={handleUpdateStatus} className="w-full">
            Vista breytingar
          </Button>
        )}

        {/* Messages Timeline */}
        <div>
          <h4 className="mb-4 text-sm font-medium text-gray-900">
            Skilaboð ({messages.length})
          </h4>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`rounded-lg p-4 ${
                  message.is_internal
                    ? 'bg-yellow-50 border border-yellow-200'
                    : message.is_customer
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-900">
                    {message.user_name || 'Gestur'}
                    {message.is_internal && (
                      <Badge className="ml-2 bg-yellow-200 text-yellow-900">Innra</Badge>
                    )}
                  </span>
                  <span className="text-gray-500">{formatDateTime(message.created_at)}</span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.message}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Reply Form */}
        <form onSubmit={handleSubmitReply} className="space-y-4">
          <Textarea
            label="Svar"
            placeholder="Skrifaðu skilaboð..."
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            rows={4}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                className="rounded border-gray-300 text-litla-primary focus:ring-litla-primary"
              />
              <span>Innri athugasemd (viðskiptavinur sér ekki)</span>
            </label>
            <Button type="submit" disabled={!replyMessage.trim() || submitting}>
              <Send className="h-4 w-4" />
              {submitting ? 'Sendi...' : 'Senda'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
